<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Partner Registry</span>
      <div class="d-flex align-center">
        <TertiaryButton
          class="mr-2"
          size="small"
          :loading="loading"
          :disabled="loading"
          prepend-icon="mdi-refresh"
          :text="''"
          @click="fetchPartnerRegistry"
        />
        <TertiaryButton
          class="mr-2"
          size="small"
          :prepend-icon="sortAscending ? 'mdi-sort-ascending' : 'mdi-sort-descending'"
          :text="''"
          @click="sortAscending = !sortAscending"
        />
        <TertiaryButton
          size="small"
          :prepend-icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          :text="''"
          @click="expanded = !expanded"
        />
      </div>
    </v-card-title>
    <v-expand-transition>
      <v-card-text v-show="expanded">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <p class="mt-2">Loading partner registry...</p>
        </div>

        <!-- Error State -->
        <v-alert
          v-else-if="error"
          type="error"
          class="mb-4"
        >
          <strong>Registry Error:</strong> {{ error }}
        </v-alert>

        <!-- No Registry Entries State -->
        <div v-else-if="entries.length === 0" class="text-center py-4">
          <p>No partner registry entries found</p>
        </div>

        <!-- Registry Entries List -->
        <template v-else>
          <!-- Registry Count -->
          <div class="mb-4">
            <strong>Partner Registry Summary:</strong> {{ entries.length }} entries across {{ groupedEntries.length }} partners
          </div>

          <!-- Summary Table -->
          <v-table>
            <thead>
              <tr>
                <th
                  class="cursor-pointer"
                  @click="handleSort('rank')"
                >
                  <div class="d-flex align-center">
                    Rank
                    <v-icon
                      v-if="sortBy === 'rank'"
                      size="small"
                      class="ml-1"
                    >
                      {{ sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="cursor-pointer"
                  @click="handleSort('owner')"
                >
                  <div class="d-flex align-center">
                    Owner
                    <v-icon
                      v-if="sortBy === 'owner'"
                      size="small"
                      class="ml-1"
                    >
                      {{ sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="cursor-pointer"
                  @click="handleSort('eeg')"
                >
                  <div class="d-flex align-center">
                    EEG (RC)
                    <v-icon
                      v-if="sortBy === 'eeg'"
                      size="small"
                      class="ml-1"
                    >
                      {{ sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="cursor-pointer"
                  @click="handleSort('beg')"
                >
                  <div class="d-flex align-center">
                    BEG (CC)
                    <v-icon
                      v-if="sortBy === 'beg'"
                      size="small"
                      class="ml-1"
                    >
                      {{ sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="cursor-pointer"
                  @click="handleSort('gea')"
                >
                  <div class="d-flex align-center">
                    GEA (GC)
                    <v-icon
                      v-if="sortBy === 'gea'"
                      size="small"
                      class="ml-1"
                    >
                      {{ sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                  </div>
                </th>
                <th
                  class="cursor-pointer"
                  @click="handleSort('total')"
                >
                  <div class="d-flex align-center">
                    Total
                    <v-icon
                      v-if="sortBy === 'total'"
                      size="small"
                      class="ml-1"
                    >
                      {{ sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(group, index) in sortedGroups"
                :key="group.owner"
                :class="{ 'highlight-row': group.owner === 'nahstrom_hmv' }"
              >
                <td>
                  <v-chip
                    :color="getRankColor(index + 1)"
                    size="small"
                    variant="flat"
                  >
                    {{ index + 1 }}
                  </v-chip>
                </td>
                <td>
                  <strong>{{ group.owner }}</strong>
                  <div class="text-caption text-grey">
                    {{ group.entries.length }} entries
                  </div>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <span class="font-weight-bold">{{ group.summary.eeg }}</span>
                    <v-chip
                      v-if="getRanking('eeg', group.summary.eeg) <= 3"
                      :color="getRankColor(getRanking('eeg', group.summary.eeg))"
                      size="x-small"
                      class="ml-1"
                    >
                      #{{ getRanking('eeg', group.summary.eeg) }}
                    </v-chip>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <span class="font-weight-bold">{{ group.summary.beg }}</span>
                    <v-chip
                      v-if="getRanking('beg', group.summary.beg) <= 3"
                      :color="getRankColor(getRanking('beg', group.summary.beg))"
                      size="x-small"
                      class="ml-1"
                    >
                      #{{ getRanking('beg', group.summary.beg) }}
                    </v-chip>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <span class="font-weight-bold">{{ group.summary.gea }}</span>
                    <v-chip
                      v-if="getRanking('gea', group.summary.gea) <= 3"
                      :color="getRankColor(getRanking('gea', group.summary.gea))"
                      size="x-small"
                      class="ml-1"
                    >
                      #{{ getRanking('gea', group.summary.gea) }}
                    </v-chip>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <span class="font-weight-bold">{{ group.summary.total }}</span>
                    <v-chip
                      v-if="getRanking('total', group.summary.total) <= 3"
                      :color="getRankColor(getRanking('total', group.summary.total))"
                      size="x-small"
                      class="ml-1"
                    >
                      #{{ getRanking('total', group.summary.total) }}
                    </v-chip>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>
      </v-card-text>
    </v-expand-transition>
  </v-card>
</template>

<script setup>
import { ref, inject, computed } from 'vue';
import xpMessengerService from '@/services/xpMessengerService';

const $log = inject('log');

const entries = ref([]);
const loading = ref(false);
const error = ref(null);
const sortAscending = ref(false); // Default to descending for rankings
const sortBy = ref('total'); // Default sort by total
const expanded = ref(true);

/**
 * Groups entries by owner and calculates sums for different partner ID patterns
 */
const groupedEntries = computed(() => {
  if (!entries.value.length) return [];

  // Group entries by owner
  const grouped = entries.value.reduce((groups, entry) => {
    if (!groups[entry.owner]) {
      groups[entry.owner] = [];
    }
    groups[entry.owner].push(entry);
    return groups;
  }, {});

  // Convert to array and calculate summaries
  const result = Object.entries(grouped).map(([owner, ownerEntries]) => {
    const summary = {
      eeg: 0, // Sum of partner IDs starting with "rc"
      beg: 0, // Sum of partner IDs starting with "cc"
      gea: 0  // Sum of partner IDs starting with "gc"
    };

    // Calculate sums based on partner ID patterns
    ownerEntries.forEach(entry => {
      const partnerId = entry.partnerId.toLowerCase();
      if (partnerId.startsWith('rc')) {
        summary.eeg++;
      } else if (partnerId.startsWith('cc')) {
        summary.beg++;
      } else if (partnerId.startsWith('gc')) {
        summary.gea++;
      }
    });

    // Calculate total
    summary.total = summary.eeg + summary.beg + summary.gea;

    return {
      owner,
      entries: ownerEntries,
      summary
    };
  });

  return result;
});

/**
 * Sorted groups based on current sort criteria
 */
const sortedGroups = computed(() => {
  const groups = [...groupedEntries.value];

  return groups.sort((a, b) => {
    let aValue, bValue;

    if (sortBy.value === 'owner') {
      aValue = a.owner;
      bValue = b.owner;
    } else if (sortBy.value === 'rank') {
      // For rank, we need to calculate the rank based on total
      const totalRanked = [...groups].sort((x, y) => y.summary.total - x.summary.total);
      aValue = totalRanked.findIndex(item => item.owner === a.owner) + 1;
      bValue = totalRanked.findIndex(item => item.owner === b.owner) + 1;
    } else {
      aValue = a.summary[sortBy.value];
      bValue = b.summary[sortBy.value];
    }

    if (sortAscending.value) {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
});

/**
 * Handles column header click for sorting
 * @param {string} column - The column to sort by
 */
const handleSort = (column) => {
  if (sortBy.value === column) {
    // If clicking the same column, toggle sort direction
    sortAscending.value = !sortAscending.value;
  } else {
    // If clicking a different column, set it as sort column and default to descending
    sortBy.value = column;
    sortAscending.value = false;
  }
};

/**
 * Gets the ranking for a specific field and value
 * @param {string} field - The field to rank by
 * @param {number} value - The value to find ranking for
 * @returns {number} The ranking (1-based)
 */
const getRanking = (field, value) => {
  const sorted = [...groupedEntries.value].sort((a, b) => {
    return b.summary[field] - a.summary[field]; // Descending order
  });

  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].summary[field] === value) {
      return i + 1;
    }
  }
  return 0;
};

/**
 * Gets color for ranking chips
 * @param {number} rank - The ranking position
 * @returns {string} Color for the chip
 */
const getRankColor = (rank) => {
  switch (rank) {
    case 1:
      return 'gold';
    case 2:
      return 'grey';
    case 3:
      return 'brown';
    default:
      return 'default';
  }
};

/**
 * Fetches partner registry entries
 */
const fetchPartnerRegistry = async () => {

  loading.value = true;
  error.value = null;

  try {
    $log.debug(`Fetching partner registry`);
    entries.value = await xpMessengerService.getPartnerRegistry($log);
    $log.debug(`Successfully fetched ${entries.value.length} partner registry entries`);
  } catch (err) {
    $log.error('Error fetching partner registry:', err);
    error.value = err.message || 'Failed to fetch partner registry';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.highlight-row {
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.highlight-row:hover {
  background-color: rgba(25, 118, 210, 0.12) !important;
}
</style>
